import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Trash2, Save } from 'lucide-react';
import {
  inquiriesApi,
  INQUIRY_CATEGORY_LABEL,
  INQUIRY_SOURCE_LABEL,
  INQUIRY_STATUS_LABEL,
  type InquiryStatus,
} from '@/services/api';
import {
  DescList,
  PageHeader,
  Section,
  Select,
  LoadingState,
  ErrorState,
} from '@/components/common';

const STATUS_ORDER: InquiryStatus[] = [
  'RECEIVED',
  'IN_PROGRESS',
  'DONE',
  'REJECTED',
];

export function InquiryDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const numericId = Number(id);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['inquiry', numericId],
    queryFn: () => inquiriesApi.getInquiryDetail(numericId),
    enabled: Number.isFinite(numericId),
    staleTime: 30 * 1000,
  });

  const [status, setStatus] = useState<InquiryStatus>('RECEIVED');
  const [memo, setMemo] = useState('');
  const [answer, setAnswer] = useState('');

  useEffect(() => {
    if (data) {
      setStatus(data.status);
      setMemo(data.adminMemo ?? '');
      setAnswer(data.answer ?? '');
    }
  }, [data]);

  const updateMutation = useMutation({
    mutationFn: () =>
      inquiriesApi.updateStatus(numericId, { status, adminMemo: memo, answer }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['inquiry', numericId] });
      await queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      alert('처리 상태가 저장되었습니다.');
    },
    onError: err => {
      alert(err instanceof Error ? err.message : '저장 실패');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => inquiriesApi.deleteInquiry(numericId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      alert('문의가 삭제되었습니다.');
      navigate('/inquiries');
    },
    onError: err => {
      alert(err instanceof Error ? err.message : '삭제 실패');
    },
  });

  if (isLoading) {
    return <LoadingState />;
  }
  if (error) {
    return <ErrorState error={error} onRetry={() => refetch()} />;
  }
  if (!data) {
    return <ErrorState error={new Error('문의를 찾을 수 없습니다.')} />;
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="문의사항 상세"
        right={
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <ArrowLeft size={16} />
              뒤로가기
            </button>
            <button
              type="button"
              onClick={() => {
                if (window.confirm('해당 문의를 삭제하시겠습니까?')) {
                  deleteMutation.mutate();
                }
              }}
              disabled={deleteMutation.isPending}
              className="inline-flex items-center gap-1 rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-60"
            >
              <Trash2 size={16} />
              {deleteMutation.isPending ? '삭제 중...' : '삭제'}
            </button>
          </div>
        }
      />

      <Section title="기본 정보">
        <DescList
          items={[
            { label: '문의 ID', value: data.id },
            {
              label: '구분',
              value:
                data.categoryLabel ??
                INQUIRY_CATEGORY_LABEL[data.category] ??
                data.category,
            },
            {
              label: '출처',
              value:
                data.sourceAppLabel ??
                INQUIRY_SOURCE_LABEL[data.sourceApp] ??
                data.sourceApp,
            },
            { label: '세부 유형', value: data.subType ?? '-' },
            {
              label: '대상 매장',
              value: data.companyName
                ? `${data.companyName}${data.companySeq ? ` (#${data.companySeq})` : ''}`
                : data.companySeq
                  ? `#${data.companySeq}`
                  : '-',
            },
            {
              label: '작성자',
              value: data.writerUserSeq ? `회원 #${data.writerUserSeq}` : '-',
            },
            { label: '연락처', value: data.contact ?? '-' },
            {
              label: '현재 상태',
              value:
                data.statusLabel ??
                INQUIRY_STATUS_LABEL[data.status] ??
                data.status,
            },
            { label: '접수일', value: data.createdAt },
            { label: '최종 수정일', value: data.updatedAt ?? '-' },
          ]}
        />
      </Section>

      <Section title="문의 내용">
        <p className="whitespace-pre-wrap rounded-md border border-gray-100 bg-gray-50 p-3 text-sm text-gray-800">
          {data.content}
        </p>
      </Section>

      <Section title="처리">
        <div className="space-y-3">
          <div className="max-w-xs">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              상태
            </label>
            <Select
              value={status}
              onChange={e => setStatus(e.target.value as InquiryStatus)}
            >
              {STATUS_ORDER.map(s => (
                <option key={s} value={s}>
                  {INQUIRY_STATUS_LABEL[s]}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              보호자에게 보이는 답변{' '}
              <span className="font-normal text-gray-400">
                (선택 · 보호자 문의 내역에 노출)
              </span>
            </label>
            <textarea
              value={answer}
              onChange={e => setAnswer(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="예) 알려주셔서 감사합니다. 전화번호 정정 반영했어요 🙏"
              className="w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              처리 메모{' '}
              <span className="font-normal text-gray-400">
                (내부용 · 보호자 비노출)
              </span>
            </label>
            <textarea
              value={memo}
              onChange={e => setMemo(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="내부 처리 내용/사유 (보호자에게 보이지 않습니다)."
              className="w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <button
            type="button"
            onClick={() => updateMutation.mutate()}
            disabled={updateMutation.isPending}
            className="inline-flex items-center gap-1 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-600 disabled:opacity-60"
          >
            <Save size={16} />
            {updateMutation.isPending ? '저장 중...' : '상태 저장'}
          </button>
        </div>
      </Section>
    </div>
  );
}
